import React, { ChangeEvent, useEffect, useState } from "react";

import {
  TableMetadata,
  useTable,
  Button,
  Typography,
  Modal,
  TimePicker,
  DatePicker,
  TimePickerEvent,
  InfoPanel,
  ButtonLink,
  ArrowButton,
} from "@king-ict/eupisi-ui/components";
import { ReactComponent as ArrowBtnLeft} from "@king-ict/eupisi-ui/components/Icon/ArrowBtnLeft.svg";
import { ReactComponent as ArrowBtnRight} from "@king-ict/eupisi-ui/components/Icon/ArrowBtnRight.svg";
import { ReactComponent as CalendarDate } from "@king-ict/eupisi-ui/components/Icon/CalendarDate.svg";
import { ReactComponent as CalendarDatePrimary } from "@king-ict/eupisi-ui/components/Icon/CalendarDatePrimary.svg";
import { ImportantDate } from "@king-ict/eupisi-ui/models";
import { hrDate, hrShortTime, nameof } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import Calendar from "react-calendar";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { useHistory } from "react-router";

import {
  getRegularImportantDates,
  getImportantDateDetails,
  updateRegularImportantDates,
} from "../../../api";
import { importantDateListRoute, useTypedSelector } from "../../../app";
import {
  AdministrationList,
  AdministrationListFiltersData,
} from "../../../components";
import { useBusinessRule } from "../../../hooks";
import { PedagogicalYearImportantDate } from "../../../models";
import styles from "./RegularImportantDateUpdate.module.scss";

export const RegularImportantDateUpdate = () => {
  const [
    importantDates,
    setImportantDates,
  ] = useState<PedagogicalYearImportantDate>({
    importantDates: [] as ImportantDate[],
  } as PedagogicalYearImportantDate);

  const currentPedagogicalYear = useTypedSelector(
    (state) => state.application.pedagogicalYear
  );

  const [
    importantDateDetails,
    setImportantDateDetails,
  ] = useState<ImportantDate>({} as ImportantDate);

  const { t } = useTranslation();
  const history = useHistory();

  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [hideDateFrom, setHideDateFrom] = useState<boolean>(false);

  const { canUpdateImportantDates} = useBusinessRule();

  const { id } = useParams<{ id: string }>();
  const [detailsModalOpen, setDetailsModalOpen] = React.useState(false);

  const [confirmSuccessModalOpen, setConfirmSuccessModalOpen] = React.useState(
    false
  );

  const [cancelSuccessModalOpen, setCancelSuccessModalOpen] = React.useState(
    false
  );

  const handleToggleCancelModal = () => {
    setCancelSuccessModalOpen((prev) => !prev);
  };

  const handleConfirmCancelModal = () => {
    history.push(importantDateListRoute);
  };

  const handleToggleMessageModal = () => {
    setConfirmSuccessModalOpen((prev) => !prev);
  };

  const handleConfirmMessageModal = () => {
    history.push(importantDateListRoute);
  };

  const isUpdteEnabled =canUpdateImportantDates();

  useEffect(() => {
    (async () => {
      if (id) {
        setImportantDates(await getRegularImportantDates(+id));
      }
    })();
  }, [id]);

  const [validationErrorText, setValidationErrorText] = useState<string>();

  const handleOpenDetails = async (item: ImportantDate) => {
    setCurrentDate(new Date());
    setHideDateFrom(false);
    if (
      item.dateTimeFrom &&
      !item.isEdited &&
      currentDate > new Date(item?.dateTimeFrom)
    ) {
      setHideDateFrom(true);
    }

    if (
      isUpdteEnabled &&
      (!item.dateTimeTo ||
      (item.dateTimeTo && new Date() < new Date(item.dateTimeTo)))
    ) {
      if (item.isEdited) {
        setImportantDateDetails(item);
      } else {
        const importantDateDetailsDto = await getImportantDateDetails(
          item.id
        ).then((res) => {
          return {
            ...res,
            dateTimeFrom: res.dateTimeFrom
              ? new Date(res.dateTimeFrom)
              : new Date(),
            dateTimeTo: res.dateTimeTo ? new Date(res.dateTimeTo) : new Date(),
          };
        });
        setImportantDateDetails(importantDateDetailsDto);
      }
      setValidationErrorText(undefined);
      setDetailsModalOpen(true);
    }
  };

  const onDateFromChange = (newDate: Date) => {
    setImportantDateDetails((prev) => {
      return {
        ...prev,
        dateTimeFrom: new Date(
          newDate.getFullYear(),
          newDate.getMonth(),
          newDate.getDate(),
          prev.dateTimeFrom!.getHours(),
          prev.dateTimeFrom!.getMinutes(),
          prev.dateTimeFrom!.getSeconds()
        ),
      };
    });
    setValidationErrorText(undefined);
  };

  const onDateToChange = (newDate: Date) => {
    setImportantDateDetails((prev) => {
      return {
        ...prev,
        dateTimeTo: new Date(
          newDate.getFullYear(),
          newDate.getMonth(),
          newDate.getDate(),
          prev.dateTimeTo!.getHours(),
          prev.dateTimeTo!.getMinutes(),
          prev.dateTimeTo!.getSeconds()
        ),
      };
    });
    setValidationErrorText(undefined);
  };

  const onTimeFromChange = (event: ChangeEvent<TimePickerEvent>) => {
    const { value } = event.target;
    setImportantDateDetails((prev) => {
      return {
        ...prev,
        dateTimeFrom: new Date(
          prev.dateTimeFrom ? prev.dateTimeFrom.getFullYear() : 2021,
          prev.dateTimeFrom!.getMonth(),
          prev.dateTimeFrom!.getDate(),
          value?.getHours(),
          value?.getMinutes(),
          value?.getSeconds()
        ),
        dateTimeTo:
          value &&
          new Date(
            prev.dateTimeFrom!.getFullYear(),
            prev.dateTimeFrom!.getMonth(),
            prev.dateTimeFrom!.getDate(),
            value?.getHours(),
            value?.getMinutes(),
            value?.getSeconds()
          ) >= new Date(prev.dateTimeTo!)
            ? new Date(
                prev.dateTimeTo!.getFullYear(),
                prev.dateTimeTo!.getMonth(),
                prev.dateTimeTo!.getDate(),
                value?.getHours(),
                value?.getMinutes(),
                value?.getSeconds()
              )
            : new Date(prev.dateTimeTo!),
      };
    });
    setValidationErrorText(undefined);
  };

  const onTimeToChange = (event: ChangeEvent<TimePickerEvent>) => {
    const { value } = event.target;
    setImportantDateDetails((prev) => {
      return {
        ...prev,
        dateTimeTo: new Date(
          prev.dateTimeTo!.getFullYear(),
          prev.dateTimeTo!.getMonth(),
          prev.dateTimeTo!.getDate(),
          value?.getHours(),
          value?.getMinutes(),
          value?.getSeconds()
        ),
      };
    });
    setValidationErrorText(undefined);
  };

  const handleConfirmImportantDetails = () => {
    let newImportantDates = [] as ImportantDate[];

    const importantDateDetailsTemp = importantDates.importantDates.find(
      (x) => x.id === importantDateDetails.id
    );
    const dateNowTemp = new Date();

    // TO DO: Maybe do this with validation schema?
    if (
      (importantDateDetailsTemp?.dateTimeFrom &&
        new Date(importantDateDetailsTemp?.dateTimeFrom).getTime() !==
          importantDateDetails.dateTimeFrom!.getTime() &&
        dateNowTemp > importantDateDetails.dateTimeFrom!) ||
      dateNowTemp > importantDateDetails.dateTimeTo!
    ) {
      setValidationErrorText(t("importantDates.importantDateBeforeNow"));
    } else if (
      importantDates.importantDates
        .filter(
          (x) =>
            x.id !== importantDateDetails.id &&
            x.dateTimeFrom !== undefined &&
            x.dateTimeTo !== undefined
        )
        .some((x) => {
          return (
            importantDateDetails.dateTimeFrom! <= new Date(x.dateTimeTo!) &&
            importantDateDetails.dateTimeTo! >= new Date(x.dateTimeFrom!)
          );
        })
    ) {
      setValidationErrorText(t("importantDates.noUniqueImportantDate"));
    } else if (
      new Date(importantDateDetails.dateTimeFrom!) >=
      new Date(importantDateDetails.dateTimeTo!)
    ) {
      setValidationErrorText(t("importantDates.startDate"));
    } else {
      importantDates.importantDates.forEach((x) => {
        if (x.id === importantDateDetails.id) {
          newImportantDates.push({
            ...x,
            dateTimeFrom: importantDateDetails.dateTimeFrom,
            dateTimeTo: importantDateDetails.dateTimeTo,
            isEdited: true,
          });
        } else {
          newImportantDates.push(x);
        }
      });
      setImportantDates((prev) => {
        return { ...prev, importantDates: newImportantDates };
      });

      setDetailsModalOpen(false);
    }
  };

  const handleCloseModal = () => {
    setDetailsModalOpen(!detailsModalOpen);
  };

  const handleSave = async () => {
    await updateRegularImportantDates(importantDates.importantDates).then(
      () => {
        handleToggleMessageModal();
      }
    );
  };

  const handleCancel = () => {
    const isEdited = importantDates.importantDates.some((x) => {
      return x.isEdited;
    });

    if (isEdited) {
      handleToggleCancelModal();
    } else {
      history.push(importantDateListRoute);
    }
  };

  const [tableProps] = useTable<AdministrationListFiltersData>({
    data: importantDates.importantDates,
    totalItems: importantDates.importantDates.length,
    noHover: false,
    metadata: {
      rowKey: "id",
      rowToolTip: () => {},
      columns: [
        {
          columnKey: "eventName",
          headerClassName: `${styles.tableNameColumn}`,
          cellClassName: `${styles.tableNameColumn}`,
          onCellClick: handleOpenDetails,
          header: t("importantDates.event"),
          accessor: (importantDate) => {
            return (
              <>
                <Typography
                  variant="baseBold"
                  color="base"
                  className={`${styles.marginBottom0} ${styles.cellHover}`}
                >
                  {importantDate.eventName}
                </Typography>
              </>
            );
          },
        },
        {
          columnKey: "description",
          headerClassName: `${styles.tableNameColumn}`,
          cellClassName: `${styles.tableNameColumn}`,
          onCellClick: handleOpenDetails,
          header: t("importantDates.description"),
          accessor: "description",
        },
        {
          columnKey: "dates",
          header: t("importantDates.dates"),
          headerClassName: `${styles.tableNameColumnDate}`,
          cellClassName: `${styles.tableNameColumnDate} ${styles.cellHover}`,
          onCellClick: handleOpenDetails,
          hover: true,
          accessor: (importantDate) => {
            return (
              <>
                <Typography
                  variant="baseBold"
                  color="base"
                  className={`${styles.marginBottom0} ${styles.cellHover}`}
                >
                  {importantDate.dateTimeFrom && importantDate.dateTimeTo && (
                    <>
                      <CalendarDatePrimary />
                      {`  ${hrDate(importantDate.dateTimeFrom)} ${hrShortTime(
                        importantDate.dateTimeFrom
                      )} -  ${hrDate(importantDate.dateTimeTo)} ${hrShortTime(
                        importantDate.dateTimeTo
                      )}`}
                    </>
                  )}
                </Typography>
              </>
            );
          },
        },
      ],
    } as TableMetadata<ImportantDate>,
  });

  return (
    <>
      <Row>
        <Col xs={12}>
          <div className={styles.backContainer}>
            <ArrowButton
              ariaLabel={t("accessibility.backButton")}
              backwards
              onClick={() => history.push(importantDateListRoute)}
            />
            <ButtonLink onClick={() => history.push(importantDateListRoute)}>
              {t("importantDates.backText")}
            </ButtonLink>
          </div>
        </Col>
      </Row>
      <Typography variant="XLargeBold" className={styles.title}>
        {t("importantDates.detailsTitle", {
          pedagogicalYear: importantDates?.pedagogicalYearName,
        })}
      </Typography>
      <AdministrationList
        tableProps={tableProps}
        isCreateEnabled={false}
        isDeleteEnabled={false}
        isDetailsEnabled={false}
      />
      <hr />
      {currentPedagogicalYear.id === importantDates.pedagogicalYearId && canUpdateImportantDates() &&(
        <Row className={styles.margin0}>
          <Col xs={12} className={styles.buttonsContainer}>
            <Button color="primary" outline onClick={handleCancel}>
              {t("common.quit")}
            </Button>
            <Button color="primary" onClick={handleSave}>
              {t("common.save")}
            </Button>
          </Col>
        </Row>
      )}
      <Modal
        type={"confirm"}
        title={t("common.cancelModalTitle")}
        cancelText={t("common.no")}
        confirmText={t("common.yes")}
        open={cancelSuccessModalOpen}
        onClose={handleToggleCancelModal}
        onConfirm={handleConfirmCancelModal}
      >
        {t("common.cancelModalMessage")}
      </Modal>

      <Modal
        type={"message"}
        open={confirmSuccessModalOpen}
        confirmText={t("common.close")}
        onConfirm={handleConfirmMessageModal}
        onClose={handleToggleMessageModal}
      >
        {t("common.saveSuccess")}
      </Modal>

      {importantDateDetails &&
        importantDateDetails.dateTimeFrom &&
        importantDateDetails.dateTimeTo && (
          <Modal
            type={"confirm"}
            title={importantDateDetails?.eventName}
            variant="medium"
            open={detailsModalOpen}
            confirmText={t("common.save")}
            cancelText={t("common.quit")}
            onClose={handleCloseModal}
            onConfirm={handleConfirmImportantDetails}
          >
            <div className={styles.modalMessageContent}>
              {importantDateDetails?.description}
            </div>
            <Row className={styles.margin0}>
              {validationErrorText && (
                <InfoPanel
                  color="danger"
                  message={validationErrorText}
                  fullWidth
                  className={styles.info}
                ></InfoPanel>
              )}

              <Col md={6} className={styles.informationLayout}>
                <Typography
                  variant="base"
                  className={styles.margin0}
                  color="light"
                >
                  {t("importantDates.dateFrom")}
                </Typography>
              </Col>
              <Col md={6} className={styles.informationLayout}>
                <Typography
                  variant="base"
                  className={`${styles.margin0} ${styles.leftPadding}`}
                  color="light"
                >
                  {t("importantDates.dateTo")}
                </Typography>
              </Col>
            </Row>
            <Row className={styles.margin0}>
              <Col
                md={1}
                className={`${styles.informationLayout} ${styles.marginTop4}`}
              >
                <CalendarDate />
              </Col>
              <Col
                md={2}
                className={`${styles.informationLayout} ${styles.marginTop8}`}
              >
                <Typography
                  variant="baseBold"
                  className={styles.margin0}
                  color="base"
                >
                  {hrDate(importantDateDetails.dateTimeFrom!)}
                </Typography>
              </Col>
              <Col md={3} className={styles.informationLayout}>
                <TimePicker
                  value={importantDateDetails.dateTimeFrom!}
                  disabled={hideDateFrom}
                  name={nameof<typeof importantDateDetails>("dateTimeFrom")}
                  onChange={onTimeFromChange}
                />
              </Col>
              <Col
                md={1}
                className={`${styles.informationLayout} ${styles.leftPadding} ${styles.marginTop4}`}
              >
                <CalendarDate />
              </Col>
              <Col
                md={2}
                className={`${styles.informationLayout} ${styles.marginTop8}`}
              >
                <Typography
                  variant="baseBold"
                  className={styles.margin0}
                  color="base"
                >
                  {hrDate(importantDateDetails?.dateTimeTo!)}
                </Typography>
              </Col>
              <Col md={3} className={styles.informationLayout}>
                <TimePicker
                  value={importantDateDetails.dateTimeTo!}
                  label={""}
                  name={nameof<typeof importantDateDetails>("dateTimeTo")}
                  onChange={onTimeToChange}
                />
              </Col>
            </Row>
            <Row className={styles.margin0}>
              <Col md={6} className={styles.informationLayout}>
                {!hideDateFrom && (
                  <Calendar
                    locale={"hr-HR"}
                    value={importantDateDetails?.dateTimeFrom!}
                    onChange={onDateFromChange}
                    prevLabel={<ArrowBtnLeft />}
                    nextLabel={<ArrowBtnRight />}
                    minDate={
                      new Date(
                        currentDate.getFullYear(),
                        currentDate.getMonth(),
                        currentDate.getDate()
                      )
                    }
                    maxDate={
                      new Date(
                        importantDateDetails?.dateTimeTo!.getFullYear(),
                        importantDateDetails?.dateTimeTo!.getMonth(),
                        importantDateDetails?.dateTimeTo!.getDate()
                      )
                    }
                  />
                )}
              </Col>
              <Col
                md={6}
                className={`${styles.informationLayout} ${styles.leftPadding}`}
              >
                <Calendar
                  locale={"hr-HR"}
                  value={importantDateDetails?.dateTimeTo!}
                  onChange={onDateToChange}
                  prevLabel={<ArrowBtnLeft />}
                  nextLabel={<ArrowBtnRight />}
                  minDate={
                    currentDate > importantDateDetails?.dateTimeFrom!
                      ? new Date(
                          currentDate.getFullYear(),
                          currentDate.getMonth(),
                          currentDate.getDate()
                        )
                      : new Date(
                          importantDateDetails?.dateTimeFrom!.getFullYear(),
                          importantDateDetails?.dateTimeFrom!.getMonth(),
                          importantDateDetails?.dateTimeFrom!.getDate()
                        )
                  }
                />
              </Col>
            </Row>
            <hr className={styles.line} />
          </Modal>
        )}
    </>
  );
};
